import { createComponent, createContext, useContext, type ParentProps } from "solid-js"
import { localMode } from "@/transport/local-mode"
import {
  deleteNotificationContact,
  listNotificationContacts,
  saveNotificationContact,
  verifyNotificationContact,
  type ContactChannel,
  type NotificationContact,
} from "@/transport/notification-contacts"
import { createQuery } from "@/utils/query"

/**
 * The account's email address and phone number for chat notifications. Each
 * chat's bell offers a channel once its contact is verified; Settings adds,
 * verifies, and removes them. A local Mux has no account to send from.
 */
function createNotificationContacts() {
  const query = createQuery(() => !localMode(), (_key, { signal }) => listNotificationContacts(signal))
  const contact = (channel: ContactChannel) => query()?.contacts.find((item) => item.channel === channel)
  const replace = (channel: ContactChannel, next: NotificationContact | undefined) => {
    const current = query()
    if (!current) return
    const contacts = current.contacts.filter((item) => item.channel !== channel)
    query.mutate({ ...current, contacts: next ? [...contacts, next] : contacts })
  }
  return {
    loaded: () => query() !== undefined,
    error: () => query.error,
    refresh: () => query.refresh(),
    available: (channel: ContactChannel) => query()?.available[channel] === true,
    contact,
    /** The channel can receive messages: verified and not stopped. */
    deliverable: (channel: ContactChannel) => {
      const item = contact(channel)
      return !!item?.verified && !item.disabledReason
    },
    async save(channel: ContactChannel, input: { address: string; details?: boolean }) {
      replace(channel, await saveNotificationContact(channel, input))
    },
    async verify(channel: ContactChannel, code: string) {
      replace(channel, await verifyNotificationContact(channel, code))
    },
    async remove(channel: ContactChannel) {
      await deleteNotificationContact(channel)
      replace(channel, undefined)
    },
  }
}

export type NotificationContactsStore = ReturnType<typeof createNotificationContacts>

const NotificationContactsContext = createContext<NotificationContactsStore>()

export function NotificationContactsProvider(props: ParentProps) {
  return createComponent(NotificationContactsContext.Provider, {
    value: createNotificationContacts(),
    get children() {
      return props.children
    },
  })
}

export function useNotificationContacts() {
  const store = useContext(NotificationContactsContext)
  if (!store) throw new Error("useNotificationContacts must be used within NotificationContactsProvider")
  return store
}
